import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Search, SlidersHorizontal, Columns3, Eye, EyeOff, Loader2, Table2, Plus, X } from "lucide-react";
import { toast } from "sonner";

const ACCESS = ["staff", "accountant"];
const STAFF_TYPES = ["Teaching", "Non-Teaching"];
const GENDERS = ["Male", "Female", "Other"];
const STATUS = ["Active", "Disabled"];
const DEFAULT_ROLES = ["Principal", "Vice-Principal", "Administrator", "Academic Head", "Teacher"];

const FIELDS = [
  { key: "name", label: "Name", w: 180 },
  { key: "username", label: "Username", w: 140 },
  { key: "role", label: "Access Level", type: "select", options: ACCESS, w: 130 },
  { key: "designation", label: "Role", type: "roles", w: 150 },
  { key: "staff_type", label: "Staff Type", type: "select", options: STAFF_TYPES, w: 130 },
  { key: "phone", label: "Phone", w: 130 },
  { key: "email", label: "Email", w: 180 },
  { key: "gender", label: "Gender", type: "select", options: GENDERS, w: 110 },
  { key: "dob", label: "DOB", type: "date", w: 150 },
  { key: "qualification", label: "Qualification", w: 160 },
  { key: "joining_date", label: "Joining Date", type: "date", w: 150 },
  { key: "salary", label: "Salary", type: "number", w: 110 },
  { key: "status", label: "Status", type: "select", options: STATUS, w: 110 },
  { key: "father_name", label: "Father", w: 150 },
  { key: "experience", label: "Experience", w: 120 },
  { key: "last_position", label: "Last Position", w: 140 },
  { key: "last_organisation", label: "Last Org", w: 150 },
  { key: "aadhar_number", label: "Aadhar", w: 140 },
  { key: "account_number", label: "Account No", w: 140 },
  { key: "ifsc_code", label: "IFSC", w: 120 },
  { key: "bank_name", label: "Bank", w: 130 },
  { key: "pan_no", label: "PAN", w: 120 },
];

const DEFAULT_HIDDEN = ["email", "gender", "dob", "qualification", "father_name", "experience", "last_position", "last_organisation", "aadhar_number", "account_number", "ifsc_code", "bank_name", "pan_no"];

const FILTER_FIELDS = [
  { key: "role", label: "Access Level", type: "select", options: ACCESS },
  { key: "designation", label: "Role", type: "roles" },
  { key: "staff_type", label: "Staff Type", type: "select", options: STAFF_TYPES },
  { key: "status", label: "Status", type: "select", options: STATUS },
];

function EditCell({ id, field, value, type, roles, isAdmin, options, flashKey, onSave }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const flashed = flashKey === `${id}-${field.key}`;
  const base = `w-full h-8 px-2 rounded border text-sm bg-white outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 ${flashed ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`;
  const testid = `cell-${field.key}-${id}`;

  if (field.key === "role" && isAdmin) {
    return <span className="inline-flex items-center px-2 h-8 text-sm text-violet-700 font-medium" data-testid={testid}>admin</span>;
  }
  if (type === "roles") {
    return (
      <select value={v} onChange={(e) => { setV(e.target.value); onSave(id, field.key, e.target.value); }} className={base} data-testid={testid}>
        <option value=""></option>
        {[...new Set([...roles, ...(v ? [v] : [])])].map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (type === "select") {
    const opts = [...new Set([...((options && options.length ? options : field.options) || []), ...(v ? [v] : [])])];
    return (
      <select value={v} onChange={(e) => { setV(e.target.value); onSave(id, field.key, e.target.value); }} className={base} data-testid={testid}>
        <option value=""></option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input type={type || "text"} value={v} onChange={(e) => setV(e.target.value)}
      onBlur={() => { if ((v ?? "") !== (value ?? "")) onSave(id, field.key, v); }}
      className={base} data-testid={testid} />
  );
}

export default function BulkEditStaff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [hidden, setHidden] = useState(DEFAULT_HIDDEN);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState([]);
  const [applied, setApplied] = useState({});
  const [flashKey, setFlashKey] = useState(null);
  const [activeRow, setActiveRow] = useState(null);

  useEffect(() => {
    api.get("/staff-roles").then((r) => setRoles(r.data.roles || DEFAULT_ROLES)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/staff").then((r) => setStaff(r.data || [])).catch(() => setStaff([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (id, field, value) => {
    try {
      await api.patch(`/staff/${id}/field`, { field, value });
      setStaff((p) => p.map((s) => s.id === id ? { ...s, [field]: value } : s));
      setFlashKey(`${id}-${field}`);
      setTimeout(() => setFlashKey(null), 900);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); load(); }
  };

  const cols = FIELDS.filter((f) => !hidden.includes(f.key));
  const toggleCol = (k) => setHidden(hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k]);

  const distinctVals = (key) => Array.from(new Set(staff.map((s) => (s[key] == null ? "" : String(s[key])).trim()).filter(Boolean)));
  const optionsFor = (field) => {
    const fromDb = distinctVals(field.key).filter((x) => field.key !== "role" || x !== "admin");
    return Array.from(new Set([...(field.options || []), ...fromDb]));
  };

  const addFilterRow = () => setFilters([...filters, { key: "role", value: "" }]);
  const updateFilter = (i, patch) => setFilters(filters.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const removeFilter = (i) => setFilters(filters.filter((_, idx) => idx !== i));
  const applyFilters = () => {
    const obj = {};
    filters.forEach((f) => { if (f.value !== "" && f.value != null) obj[f.key] = f.value; });
    setApplied(obj); setPage(1);
  };
  const clearFilters = () => { setFilters([]); setApplied({}); setPage(1); };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return staff.filter((s) => {
      if (q && ![s.name, s.username, s.phone, s.email, s.designation].some((v) => (v || "").toLowerCase().includes(q))) return false;
      for (const [k, val] of Object.entries(applied)) {
        if (k === "staff_type") { if ((s.staff_type || "Teaching") !== val) return false; }
        else if ((s[k] || "") !== val) return false;
      }
      return true;
    });
  }, [staff, search, applied]);

  const totalPages = Math.ceil(filtered.length / limit) || 1;
  const pageRows = filtered.slice((page - 1) * limit, page * limit);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Table2 className="text-indigo-500" size={26} />Bulk Edit Staff</h1>
        <p className="text-slate-500">Edit inline — changes save automatically when you leave a cell or press Tab. No save button needed.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={showFilter || Object.keys(applied).length ? "default" : "outline"} size="sm" className="gap-1.5" onClick={() => setShowFilter(!showFilter)} data-testid="bes-filter-btn"><SlidersHorizontal size={15} />Advanced Filter</Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="bes-columns-btn"><Columns3 size={15} />Columns</Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-60 p-2 max-h-80 overflow-auto" data-testid="bes-columns-panel">
              <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">Show Columns</div>
              {FIELDS.map((f) => (
                <button key={f.key} onClick={() => toggleCol(f.key)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-sm text-left" data-testid={`bes-col-toggle-${f.key}`}>
                  {hidden.includes(f.key) ? <EyeOff size={15} className="text-slate-300" /> : <Eye size={15} className="text-indigo-600" />}
                  <span className={hidden.includes(f.key) ? "text-slate-400" : "text-slate-700"}>{f.label}</span>
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Search name, username, phone, email…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="bes-search" />
          </div>
          <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-24 h-9" data-testid="bes-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>{[25, 50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {showFilter && (
          <div className="border-t border-slate-100 pt-3 space-y-3" data-testid="bes-filter-panel">
            {filters.map((f, i) => {
              const field = FILTER_FIELDS.find((x) => x.key === f.key);
              return (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <Select value={f.key} onValueChange={(v) => updateFilter(i, { key: v, value: "" })}>
                    <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{FILTER_FIELDS.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="text-slate-400 text-sm">is</span>
                  {field?.type === "roles" ? (
                    <Select value={f.value} onValueChange={(v) => updateFilter(i, { value: v })}><SelectTrigger className="w-44 h-9"><SelectValue placeholder="Select role" /></SelectTrigger><SelectContent>{roles.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                  ) : field?.type === "select" ? (
                    <Select value={f.value} onValueChange={(v) => updateFilter(i, { value: v })}><SelectTrigger className="w-40 h-9"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                  ) : (
                    <Input className="w-44 h-9" value={f.value} onChange={(e) => updateFilter(i, { value: e.target.value })} placeholder="value" />
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => removeFilter(i)}><X size={15} /></Button>
                </div>
              );
            })}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={addFilterRow} data-testid="bes-add-filter"><Plus size={14} className="mr-1" />Add Filter</Button>
              <Button size="sm" onClick={applyFilters} data-testid="bes-apply-filter">Apply Filters</Button>
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="bes-clear-filter">Clear All</Button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          pageRows.length === 0 ? (
            <div className="py-16 text-center text-slate-400"><Table2 size={40} className="mx-auto mb-2 opacity-40" />No staff found.</div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="text-sm border-collapse">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 border border-slate-200 bg-slate-50 sticky left-0 z-20 text-left w-11" style={{ minWidth: 44, width: 44 }}>#</th>
                    {cols.map((c) => {
                      const frozen = c.key === "name";
                      return <th key={c.key} className={`px-2 py-2 border border-slate-200 bg-slate-50 text-left whitespace-nowrap ${frozen ? "sticky left-11 z-20 border-r-2 border-r-slate-300" : ""}`} style={{ minWidth: c.w }}>{c.label}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((s, idx) => (
                    <tr key={s.id} data-testid={`bes-row-${s.username}`} onFocusCapture={() => setActiveRow(s.id)} className={activeRow === s.id ? "bg-indigo-50" : ""}>
                      <td className={`px-2 py-1.5 border border-slate-200 text-slate-400 sticky left-0 z-10 tabular w-11 ${activeRow === s.id ? "bg-indigo-50" : "bg-white"}`} style={{ minWidth: 44, width: 44 }}>{(page - 1) * limit + idx + 1}</td>
                      {cols.map((c) => {
                        const frozen = c.key === "name";
                        return (
                          <td key={c.key} className={`px-1 py-1 border border-slate-200 ${frozen ? `sticky left-11 z-10 border-r-2 border-r-slate-300 ${activeRow === s.id ? "bg-indigo-50" : "bg-white"}` : ""}`} style={{ minWidth: c.w }}>
                            <EditCell id={s.id} field={c} value={s[c.key]} type={c.type} roles={roles} isAdmin={s.role === "admin"} options={c.type === "select" ? optionsFor(c) : undefined} flashKey={flashKey} onSave={save} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {filtered.length > limit && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-500">Page {page} of {totalPages} · {filtered.length} total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="bes-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="bes-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
